/**
 * The front camera, opened for a measurement and closed the moment one ends.
 *
 * The stream is never recorded, never drawn anywhere but the two working canvases, and never leaves
 * the device. Closing it also releases the screen lock, so a phone left on the results screen does
 * not sit awake with its camera light on.
 */
export interface CameraRequest {
  facingMode: 'user' | 'environment';
  width: number;
  height: number;
}

/** The front camera at as much resolution as the handset will give: the limbus is a few pixels wide, and every one of them counts. */
export const FRONT_CAMERA: CameraRequest = { facingMode: 'user', width: 1920, height: 1080 };

export type CameraFailure = 'DENIED' | 'UNAVAILABLE' | 'INSECURE';

export class CameraError extends Error {
  constructor(readonly kind: CameraFailure) {
    super(kind);
  }
}

export async function openCamera(req: CameraRequest = FRONT_CAMERA): Promise<MediaStream> {
  if (!window.isSecureContext) throw new CameraError('INSECURE');
  if (!navigator.mediaDevices?.getUserMedia) throw new CameraError('UNAVAILABLE');
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: { ideal: req.facingMode },
        width: { ideal: req.width },
        height: { ideal: req.height },
      },
    });
  } catch (error) {
    const name = (error as Error).name;
    throw new CameraError(
      name === 'NotAllowedError' || name === 'SecurityError' ? 'DENIED' : 'UNAVAILABLE',
    );
  }
}

export function stopCamera(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop());
}
